const spaceScale = [
  0, 0.5, 1, 1.5, 2, 2.5, 3, 3.5, 4, 4.5, 5, 5.5, 6, 6.5, 7, 7.5, 8, 8.5, 9,
  9.5, 10, 10.5, 11, 11.5, 12, 14, 16, 20, 24,
] as const;

type SpaceScale = (typeof spaceScale)[number];

const spaceScaleWithAuto = [...spaceScale, 'auto'] as const;

type MarginScale = (typeof spaceScaleWithAuto)[number];

type PositiveMarginTypes =
  | 'm'
  | 'mx'
  | 'my'
  | 'mt'
  | 'mr'
  | 'mb'
  | 'ml'
  | 'me'
  | 'ms';

type NegativeMarginTypes =
  | '-m'
  | '-mx'
  | '-my'
  | '-mt'
  | '-mr'
  | '-mb'
  | '-ml'
  | '-me'
  | '-ms';

type MarginTypes = PositiveMarginTypes | NegativeMarginTypes;

type PaddingGapTypes =
  | 'p'
  | 'px'
  | 'py'
  | 'pt'
  | 'pr'
  | 'pb'
  | 'pl'
  | 'pe'
  | 'ps'
  | 'gap'
  | 'gap-x'
  | 'gap-y';

type SpaceTypes = MarginTypes | PaddingGapTypes;

type SpaceValue = Record<SpaceScale, `${PaddingGapTypes}-${SpaceScale}`>;

// Positive margins carry `auto` (`m-auto` centres a block); negative margins do
// not — `-m-auto` is not a Tailwind utility and produced no CSS (VWA-169).
type MarginValue = Record<MarginScale, `${PositiveMarginTypes}-${MarginScale}`>;

type NegativeMarginValue = Record<
  SpaceScale,
  `${NegativeMarginTypes}-${SpaceScale}`
>;

type SpaceVariant<T extends SpaceTypes> = T extends NegativeMarginTypes
  ? NegativeMarginValue
  : T extends PositiveMarginTypes
    ? MarginValue
    : SpaceValue;

function generateSpaceVariants<T extends SpaceTypes>(
  spaceType: T,
): SpaceVariant<T> {
  if (spaceType.includes('m')) {
    const scale = spaceType.startsWith('-') ? spaceScale : spaceScaleWithAuto;
    return scale.reduce((acc, value) => {
      (acc as Record<PropertyKey, string>)[value] = `${spaceType}-${value}`;
      return acc;
    }, {} as SpaceVariant<T>);
  }

  return [...spaceScale].reduce((acc, scale) => {
    acc[scale] = `${spaceType}-${scale}`;
    return acc;
  }, {} as SpaceVariant<T>);
}

const spaceVariants = {
  m: generateSpaceVariants('m'),
  margin: generateSpaceVariants('m'),
  mx: generateSpaceVariants('mx'),
  marginX: generateSpaceVariants('mx'),
  my: generateSpaceVariants('my'),
  marginY: generateSpaceVariants('my'),
  mt: generateSpaceVariants('mt'),
  marginTop: generateSpaceVariants('mt'),
  mr: generateSpaceVariants('mr'),
  marginRight: generateSpaceVariants('mr'),
  mb: generateSpaceVariants('mb'),
  marginBottom: generateSpaceVariants('mb'),
  ml: generateSpaceVariants('ml'),
  marginLeft: generateSpaceVariants('ml'),
  ms: generateSpaceVariants('ms'),
  marginStart: generateSpaceVariants('ms'),
  me: generateSpaceVariants('me'),
  marginEnd: generateSpaceVariants('me'),

  mNeg: generateSpaceVariants('-m'),
  marginNeg: generateSpaceVariants('-m'),
  mxNeg: generateSpaceVariants('-mx'),
  marginXNeg: generateSpaceVariants('-mx'),
  myNeg: generateSpaceVariants('-my'),
  marginYNeg: generateSpaceVariants('-my'),
  mtNeg: generateSpaceVariants('-mt'),
  marginTopNeg: generateSpaceVariants('-mt'),
  mrNeg: generateSpaceVariants('-mr'),
  marginRightNeg: generateSpaceVariants('-mr'),
  mbNeg: generateSpaceVariants('-mb'),
  marginBottomNeg: generateSpaceVariants('-mb'),
  mlNeg: generateSpaceVariants('-ml'),
  marginLeftNeg: generateSpaceVariants('-ml'),
  msNeg: generateSpaceVariants('-ms'),
  marginStartNeg: generateSpaceVariants('-ms'),
  meNeg: generateSpaceVariants('-me'),
  marginEndNeg: generateSpaceVariants('-me'),

  p: generateSpaceVariants('p'),
  padding: generateSpaceVariants('p'),
  px: generateSpaceVariants('px'),
  paddingX: generateSpaceVariants('px'),
  py: generateSpaceVariants('py'),
  paddingY: generateSpaceVariants('py'),
  pt: generateSpaceVariants('pt'),
  paddingTop: generateSpaceVariants('pt'),
  pr: generateSpaceVariants('pr'),
  paddingRight: generateSpaceVariants('pr'),
  pb: generateSpaceVariants('pb'),
  paddingBottom: generateSpaceVariants('pb'),
  pl: generateSpaceVariants('pl'),
  paddingLeft: generateSpaceVariants('pl'),
  ps: generateSpaceVariants('ps'),
  paddingStart: generateSpaceVariants('ps'),
  pe: generateSpaceVariants('pe'),
  paddingEnd: generateSpaceVariants('pe'),

  gap: generateSpaceVariants('gap'),
  gapX: generateSpaceVariants('gap-x'),
  gapY: generateSpaceVariants('gap-y'),
};

type SpaceVariants = typeof spaceVariants;

export {
  type MarginScale,
  type SpaceScale,
  spaceScale,
  spaceScaleWithAuto,
  type SpaceVariants,
  spaceVariants,
};
